import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

export type BzAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * A person's picture, or their initials when there is no picture — never a
 * broken-image icon. The three legacy avatars (`chat-user-avatar`,
 * `user-avatar`, and an AngularJS controller) each picked a different size
 * scale; this one picks `sm`/`md`/`lg`/`xl` and does not try to be a literal
 * drop-in for any of them.
 *
 * Deliberately a plain `<img>` rather than `NgOptimizedImage`, against the
 * usual house rule. `src` here is whatever a consuming application hands
 * over at runtime, and `NgOptimizedImage` *throws* on `data:` and `blob:`
 * URLs — which is exactly how generated and placeholder avatars arrive. A
 * design-system component that crashes on a legitimate input is worse than
 * one that skips an optimisation, so the parts that do apply — lazy loading
 * and async decoding — are set directly instead.
 */
@Component({
  selector: 'bz-avatar',
  templateUrl: './bz-avatar.html',
  styleUrl: './bz-avatar.scss',
  host: {
    '[class]': 'hostClass()',
    '[attr.role]': 'name() ? "img" : null',
    '[attr.aria-label]': 'name()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzAvatar {
  readonly src = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly size = input<BzAvatarSize>('md');

  protected readonly failed = signal(false);

  constructor() {
    // A new src deserves a fresh attempt — otherwise a recycled avatar (a
    // list row that gets rebound to a different person) would stay stuck
    // showing initials because a *previous* image once failed to load.
    effect(() => {
      this.src();
      this.failed.set(false);
    });
  }

  protected readonly hostClass = computed(() => `bz-avatar bz-avatar--${this.size()}`);

  protected readonly initials = computed(() => {
    const words = (this.name() ?? '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    const first = words[0][0];
    const last = words.length > 1 ? words[words.length - 1][0] : '';
    return (first + last).toUpperCase();
  });
}
